import clsx from "clsx";
import { useEffect, useState, type ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faExclamationTriangle,
  faPenToSquare,
  faSparkles,
  faUser,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Button } from "@/components/ui/button/Button";
import {
  CC_ALERT_WARNING_BODY,
  CC_ALERT_WARNING_PANEL,
  CC_ALERT_WARNING_TITLE,
  CC_FIELD,
  CC_INSET,
  CC_PANEL,
  CC_SUCCESS_INSET,
  CC_SUCCESS_INSET_BODY,
  CC_SUCCESS_INSET_TITLE,
} from "@/components/case-checking/caseCheckingChrome";
import { clearHumanReviewPatch } from "@/components/case-checking/problemStatementUi";
import {
  agentGradeOverrideNote,
  CaseListGradeBadge,
  CASE_CHECKING_REVIEW_GRADE_BADGE,
  caseReviewBaselineGrade,
  caseReviewRollupGrade,
  ICON_CHECK_PASSED,
  type CaseCheckingGrade,
} from "@/components/case-checking/caseCheckingStatusBadges";
import { finalOutcomeId } from "@/components/case-checking/outcomeCatalog";
import type { CaseReview as ApiCaseReview, ProblemStatementHumanReview, ProblemStatementHumanReviewPatch } from "@/services/api/case-checking";

const GRADE_OPTIONS: { id: CaseCheckingGrade; label: string }[] = [
  { id: "suitable", label: "Suitable" },
  { id: "dev-needs", label: "Suitable with Dev" },
  { id: "unclear", label: "Unclear" },
  { id: "unsuitable", label: "Unsuitable" },
];

const GRADE_TAG_RE = /^@grade=([\w-]+)@\n?/;

const COLUMN_CLASS = clsx(CC_INSET, "flex min-h-[20rem] flex-col p-4");

function packOverallNotes(grade: CaseCheckingGrade, notes: string): string {
  const body = notes.replace(GRADE_TAG_RE, "").trim();
  return body ? `@grade=${grade}@\n${body}` : `@grade=${grade}@`;
}

export function unpackOverallGrade(
  review: ProblemStatementHumanReview | null | undefined,
  fallback: CaseCheckingGrade,
): CaseCheckingGrade {
  if (!review?.reviewed_at) return fallback;
  const match = review.notes.match(GRADE_TAG_RE);
  if (match && GRADE_OPTIONS.some((g) => g.id === match[1])) {
    return match[1] as CaseCheckingGrade;
  }
  if (review.call === "yes") return fallback === "dev-needs" ? "dev-needs" : "suitable";
  if (review.call === "unclear") return "unclear";
  if (review.call === "no") return "unsuitable";
  return fallback;
}

export function overallReviewNotesBody(notes: string): string {
  return notes.replace(GRADE_TAG_RE, "").trim();
}

function gradeToCall(grade: CaseCheckingGrade): ProblemStatementHumanReview["call"] {
  if (grade === "suitable" || grade === "dev-needs") return "yes";
  if (grade === "unclear") return "unclear";
  return "no";
}

function ReviewColumnHeader({
  icon,
  iconClass,
  label,
}: {
  icon: typeof faSparkles;
  iconClass: string;
  label: string;
}) {
  return (
    <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
      <FontAwesomeIcon icon={icon} className={clsx("h-3.5 w-3.5", iconClass)} />
      {label}
    </div>
  );
}

function ReviewColumnFooter({ children, leading }: { children: ReactNode; leading?: ReactNode }) {
  return (
    <div className="mt-auto flex items-center justify-between gap-2 pt-4">
      <div className="min-w-0">{leading}</div>
      <div className="flex shrink-0 justify-end gap-2">{children}</div>
    </div>
  );
}

function GradePillRow({
  value,
  onChange,
  disabled = false,
  ariaLabel = "Overall suitability grade",
}: {
  value: CaseCheckingGrade | null;
  onChange?: (next: CaseCheckingGrade) => void;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="grid grid-cols-2 gap-1.5 rounded-lg bg-slate-100 dark:bg-slate-900/40 p-1 ring-1 ring-slate-200 dark:ring-white/5 sm:grid-cols-4"
    >
      {GRADE_OPTIONS.map((opt) => {
        const active = value === opt.id;
        return (
          <Button
            key={opt.id}
            plain
            type="button"
            disabled={disabled || !onChange}
            onClick={onChange ? () => onChange(opt.id) : undefined}
            className={clsx(
              "rounded-md px-2 py-1.5 text-[11px] font-medium ring-1 ring-inset transition-colors sm:text-xs sm:px-2",
              active
                ? "bg-indigo-100 text-indigo-800 ring-indigo-300 dark:bg-indigo-500/15 dark:text-indigo-100 dark:ring-indigo-400/50"
                : "bg-transparent text-slate-400 ring-transparent hover:bg-slate-100 dark:hover:bg-white/[0.04] hover:text-slate-700 dark:hover:text-slate-200",
              (disabled || !onChange) && "cursor-default opacity-90",
            )}
            role="radio"
            aria-checked={active}
          >
            {opt.label}
          </Button>
        );
      })}
    </div>
  );
}

type SaveState = "idle" | "saving" | "saved";

export function ReviewOverallSignoffCard({
  caseReview,
  fallbackGrade,
  savedReview,
  onSave,
  saveState,
  suggestedGrade,
  rulesChangedSinceSignoff = false,
  companyId,
  onDirtyChange,
}: {
  caseReview: ApiCaseReview | null;
  fallbackGrade: CaseCheckingGrade;
  savedReview?: ProblemStatementHumanReview;
  onSave: (review: ProblemStatementHumanReviewPatch) => void;
  saveState: SaveState;
  /** Grade implied by human rule overrides — default for agree when set. */
  suggestedGrade?: CaseCheckingGrade;
  /** Rule overrides saved after the overall sign-off timestamp. */
  rulesChangedSinceSignoff?: boolean;
  companyId?: string;
  /** True until first save, or while edited draft differs from last save. */
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const overallStatementId = finalOutcomeId(companyId);
  const agentGrade = caseReviewRollupGrade(caseReview, fallbackGrade);
  const agentBaseline = caseReviewBaselineGrade(caseReview, fallbackGrade);
  const defaultHumanGrade =
    suggestedGrade !== undefined && suggestedGrade !== agentBaseline ? suggestedGrade : agentGrade;
  const agentNote = agentGradeOverrideNote(caseReview?.baseline_grade, caseReview?.final_grade);

  const [isEditing, setIsEditing] = useState(false);
  const [draftGrade, setDraftGrade] = useState<CaseCheckingGrade>(defaultHumanGrade);
  const [draftNotes, setDraftNotes] = useState("");

  const savedGrade = unpackOverallGrade(savedReview, agentGrade);
  const savedNotesBody = savedReview ? overallReviewNotesBody(savedReview.notes) : "";
  const isReviewed = Boolean(savedReview?.reviewed_at);
  const isSaving = saveState === "saving";

  useEffect(() => {
    setIsEditing(false);
    setDraftGrade(isReviewed ? savedGrade : defaultHumanGrade);
    setDraftNotes(savedNotesBody);
  }, [savedReview, agentGrade, defaultHumanGrade, isReviewed, savedGrade, savedNotesBody]);

  const formDirty =
    !isReviewed || draftGrade !== savedGrade || draftNotes.trim() !== savedNotesBody.trim();

  useEffect(() => {
    onDirtyChange?.(formDirty);
  }, [formDirty, onDirtyChange]);

  const showForm = !isReviewed || isEditing;
  const showSaved = isReviewed && !isEditing;

  const handleSave = () => {
    onSave({
      problem_statement_id: overallStatementId,
      call: gradeToCall(draftGrade),
      notes: packOverallNotes(draftGrade, draftNotes),
      reviewer_name: savedReview?.reviewer_name ?? "",
      reviewed_at: null,
    });
    setIsEditing(false);
  };

  const cancelEdit = () => {
    setDraftGrade(savedGrade);
    setDraftNotes(savedNotesBody);
    setIsEditing(false);
  };

  const handleClearReview = () => {
    if (
      !window.confirm(
        "Clear your overall review? It will show as review pending again. Your previous grade is kept in the audit trail.",
      )
    ) {
      return;
    }
    onSave(clearHumanReviewPatch(overallStatementId));
    setIsEditing(false);
  };

  const saveDisabled = isSaving || (isReviewed && isEditing && !formDirty);

  const ruleRollupDiffersFromSaved =
    suggestedGrade !== undefined && isReviewed && suggestedGrade !== savedGrade;
  const showRuleRollupStaleNotice = rulesChangedSinceSignoff && ruleRollupDiffersFromSaved;

  return (
    <article className={clsx(CC_PANEL, "space-y-4 scroll-mt-24")} data-testid={`ps-card-${overallStatementId}`}>
      <header className="space-y-1.5">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Final step · {overallStatementId}
        </p>
        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">Final Suitability Assessment</h3>
        <p className="text-sm text-slate-400">
          Based on all available evidence, is the recommendation suitable?
        </p>
      </header>

      {showRuleRollupStaleNotice ? (
        <div
          className={clsx(CC_ALERT_WARNING_PANEL, "flex items-start gap-2 px-3 py-2.5")}
          data-testid="overall-assessment-rules-changed-notice"
        >
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
          />
          <p className={clsx("text-sm", CC_ALERT_WARNING_BODY)}>
            <span className={CC_ALERT_WARNING_TITLE}>Rule edits changed since you signed off</span>
            {" — "}
            the rule rollup is now{" "}
            <span className="font-medium">
              {CASE_CHECKING_REVIEW_GRADE_BADGE[suggestedGrade]?.label ?? suggestedGrade}
            </span>
            {savedGrade !== agentGrade ? (
              <>
                , while your saved overall grade is{" "}
                <span className="font-medium">
                  {CASE_CHECKING_REVIEW_GRADE_BADGE[savedGrade]?.label ?? savedGrade}
                </span>
              </>
            ) : null}
            {agentGrade !== suggestedGrade ? (
              <>
                . The agent&apos;s overall judgment remains{" "}
                <span className="font-medium">
                  {CASE_CHECKING_REVIEW_GRADE_BADGE[agentGrade]?.label ?? agentGrade}
                </span>
              </>
            ) : null}
            . Review and re-save your overall grade if needed.
          </p>
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-2 md:items-stretch">
        <div className={COLUMN_CLASS}>
          <ReviewColumnHeader icon={faSparkles} iconClass="text-indigo-600 dark:text-indigo-300" label="Agent" />
          <CaseListGradeBadge grade={agentGrade} className="!text-base" />
          <div className="mt-3 flex min-h-0 flex-1 flex-col gap-3">
            {caseReview?.summary ? (
              <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200">{caseReview.summary}</p>
            ) : (
              <p className="text-sm text-slate-500">No agent summary yet.</p>
            )}
            {agentNote ? <p className="text-sm text-slate-400 leading-relaxed">{agentNote}</p> : null}
            {caseReview?.grade_rationale ? (
              <p className="text-sm text-slate-400 leading-relaxed">{caseReview.grade_rationale}</p>
            ) : null}
          </div>
        </div>

        <div className={COLUMN_CLASS} data-testid={`ps-human-${overallStatementId}`}>
          <ReviewColumnHeader icon={faUser} iconClass="text-indigo-600 dark:text-indigo-300" label="Your review" />

          {suggestedGrade ? (
            <p className="mb-3 text-xs text-slate-400">
              After your rule changes, the{" "}
              <span className="text-slate-600 dark:text-slate-300">rule rollup</span> is{" "}
              <span className="font-medium text-slate-700 dark:text-slate-200">
                {CASE_CHECKING_REVIEW_GRADE_BADGE[suggestedGrade]?.label ?? suggestedGrade}
              </span>
              {suggestedGrade !== agentBaseline ? (
                <>
                  {" "}
                  (agent rollup was{" "}
                  {CASE_CHECKING_REVIEW_GRADE_BADGE[agentBaseline]?.label ?? agentBaseline})
                </>
              ) : (
                <> — same as the agent&apos;s mechanical rollup</>
              )}
              {suggestedGrade !== agentGrade ? (
                <>
                  . The agent&apos;s overall judgment remains{" "}
                  {CASE_CHECKING_REVIEW_GRADE_BADGE[agentGrade]?.label ?? agentGrade}.
                </>
              ) : null}
            </p>
          ) : null}

          {showForm ? (
            <>
              <GradePillRow value={draftGrade} onChange={setDraftGrade} />
              <div className="mt-3 flex min-h-0 flex-1 flex-col">
                <label htmlFor="overall-grade-notes" className="mb-1.5 text-xs text-slate-500">
                  Notes (optional)
                </label>
                <textarea
                  id="overall-grade-notes"
                  className={clsx(
                    CC_FIELD,
                    "min-h-[8rem] flex-1 resize-none py-3 leading-relaxed focus:border-indigo-500/40 focus:outline-none focus:ring-1 focus:ring-indigo-500/30",
                  )}
                  placeholder="Add context for your overall grade if helpful."
                  value={draftNotes}
                  onChange={(e) => setDraftNotes(e.target.value)}
                />
              </div>
              <ReviewColumnFooter
                leading={
                  isReviewed ? (
                    <Button plain size="unstyled" onClick={handleClearReview} disabled={isSaving} data-testid={`ps-clear-${overallStatementId}`}>
                      Clear review
                    </Button>
                  ) : null
                }
              >
                {isReviewed ? (
                  <Button plain onClick={cancelEdit} disabled={isSaving}>
                    Cancel
                  </Button>
                ) : null}
                <Button
                  color="indigo"
                  onClick={handleSave}
                  disabled={saveDisabled}
                  data-testid={`ps-save-${overallStatementId}`}
                >
                  {isSaving ? "Saving…" : "Save"}
                </Button>
              </ReviewColumnFooter>
            </>
          ) : null}

          {showSaved ? (
            <>
              <CaseListGradeBadge grade={savedGrade} className="!text-base" />
              <div className="mt-3 flex min-h-0 flex-1 flex-col gap-3">
                <div className={clsx("flex items-start gap-2", CC_SUCCESS_INSET, "px-3 py-2.5")}>
                  <FontAwesomeIcon icon={faCircleCheck} className={clsx("mt-0.5 h-4 w-4 shrink-0", ICON_CHECK_PASSED)} />
                  <div>
                    <p className={CC_SUCCESS_INSET_TITLE}>Assessment saved</p>
                    <p className={clsx("mt-0.5 text-xs", CC_SUCCESS_INSET_BODY)}>
                      {savedGrade === agentGrade
                        ? "Your grade matches the agent's overall assessment."
                        : savedGrade === suggestedGrade && suggestedGrade !== undefined
                          ? "Your grade matches the rule rollup after your rule changes."
                          : "Your overall grade is recorded."}
                    </p>
                  </div>
                </div>
                {savedNotesBody ? (
                  <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">{savedNotesBody}</p>
                ) : null}
              </div>
              <ReviewColumnFooter
                leading={
                  <Button plain size="unstyled" onClick={handleClearReview} disabled={isSaving} data-testid={`ps-clear-${overallStatementId}`}>
                    Clear review
                  </Button>
                }
              >
                <Button
                  outline
                  onClick={() => setIsEditing(true)}
                  icon={<FontAwesomeIcon icon={faPenToSquare} />}
                  data-testid={`ps-edit-${overallStatementId}`}
                >
                  Edit review
                </Button>
              </ReviewColumnFooter>
            </>
          ) : null}
        </div>
      </div>
    </article>
  );
}
